// Anita Week 9 / Day 3 — Creator Plan: From Idea to Draft.
// Verbatim from FINAL Week 9 Part A (Creator) source-of-truth package.
// Staged on disk; Week 9 remains a locked placeholder until Days 4–5 are authored.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-9',
  dayId: 'day-3',
  status: 'active',
  title: 'План автора: от идеи к черновику / Creator Plan: From Idea to Draft',
  shortTitle: 'Идея → черновик / Idea → Draft',
  theme: 'Анита учится превращать идею в маленький план: идея, черновик, проверка, исправление, показ родителю. / Anita learns to turn an idea into a small plan: idea, draft, check, fix, show parent.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  readingLevel: 'grade-2',
  courseLayers: ['reading', 'math', 'creator-mindset', 'planning', 'draft', 'storyboard', 'time', 'spelling', 'creative', 'speech'],
  nimnyIntro:
    'Нимни показывает папку «План автора». Внутри пять шагов: идея, черновик, проверка, исправление, показ родителю. Сегодня Анита узнает: черновик — это не ошибка. Черновик — это первый шаг к полезному результату. / Nimny shows a "Creator Plan" folder. Inside there are five steps: idea, draft, check, fix, show parent. Today Anita learns: a draft is not a mistake. A draft is the first step toward a useful result.',
  goals: [
    'Понять слова план, черновик, проверка, исправление. / Understand plan, draft, check, fix.',
    'Узнать 5 шагов автора. / Learn 5 creator steps.',
    'Считать время проекта и оставшиеся шаги. / Count project time and remaining steps.',
    'Сделать storyboard из 4 кадров. / Make a 4-frame storyboard.',
    'Объяснить родителю, почему черновик полезен. / Explain why a draft is useful.'
  ],
  vocabulary: [
    { term: 'план / plan', definition: 'порядок шагов. / order of steps.' },
    { term: 'черновик / draft', definition: 'первая версия работы. / the first version of the work.' },
    { term: 'проверка / check', definition: 'посмотреть, что можно улучшить. / look at what can be improved.' },
    { term: 'исправление / fix', definition: 'сделать лучше. / make it better.' },
    { term: 'кадр / frame', definition: 'часть рисунка или истории. / a part of a drawing or story.' },
    { term: 'заголовок / title', definition: 'название работы. / the name of the work.' },
    { term: 'пример / example', definition: 'маленькая история или случай. / a small story or case.' },
    { term: 'шаг / step', definition: 'часть работы. / a part of the work.' },
    { term: 'показ / show', definition: 'показать родителю или семье. / show to a parent or family.' },
    { term: 'готово / ready', definition: 'можно объяснить и использовать. / ready to explain and use.' }
  ],
  onlineTasks: [
    { id: 'reading-words', type: 'text', title: 'Задание 1 — Слова плана / Plan Words',
      prompt: 'Прочитай по-русски:\n\nплан\nчерновик\nпроверка\nисправление\nкадр\nзаголовок\nпример\nшаг\nпоказ\nготово',
      ruPrimaryText: 'Прочитай 10 русских слов плана.',
      enHelpText: 'Read the Russian planning words first. English is only for help.',
      answer: 'Attempted Russian reading.' },
    { id: 'reading-sentences', type: 'text', title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt: 'Прочитай по-русски:\n\nЧерновик — это первый шаг.\nПроверка помогает сделать лучше.\nАвтор исправляет и пробует снова.\nГотовая работа должна быть понятной.',
      ruPrimaryText: 'Прочитай 4 короткие фразы о черновике и плане.',
      enHelpText: 'A draft is the first step. Checking helps make it better. A creator fixes and tries again. A finished work should be clear.',
      answer: 'Attempted Russian reading.' },
    { id: 'creator-steps-order', type: 'text', title: 'Задание 3 — Порядок шагов / Order the Steps',
      prompt: 'Поставь шаги автора в правильном порядке.\n\nA. Показать родителю.\nB. Сделать черновик.\nC. Придумать идею.\nD. Исправить.\nE. Проверить.\n\nОтвет запиши буквами.',
      ruPrimaryText: 'Поставь 5 шагов автора в правильном порядке.',
      enHelpText: 'Order the 5 creator steps correctly.',
      answer: 'C → B → E → D → A.' },
    { id: 'math-plan-time', type: 'text', title: 'Задание 4 — Время проекта / Project Time',
      prompt: 'У проекта 5 шагов.\nКаждый шаг занимает 8 минут.\n\nОтветь:\n1. Сколько минут займут все 5 шагов?\n2. Если Анита уже сделала 2 шага, сколько шагов осталось?\n3. Сколько минут уже прошло после 2 шагов?\n4. Сколько минут осталось?',
      ruPrimaryText: 'Реши 4 задачи про время проекта.',
      enHelpText: 'Solve 4 project-time problems.',
      answer: '1) 5 × 8 = 40 минут. 2) 5 − 2 = 3 шага. 3) 2 × 8 = 16 минут. 4) 3 × 8 = 24 минуты.' },
    { id: 'math-three-day-plan', type: 'text', title: 'Задание 5 — План на 3 дня / Three-Day Plan',
      prompt: 'Анита работает над проектом 3 дня.\n\nДень 1 — 15 минут.\nДень 2 — 15 минут.\nДень 3 — 15 минут.\n\nОтветь:\n1. Сколько всего минут?\n2. Если проект требует 40 минут, хватит ли 45 минут?\n3. Сколько минут останется?\n4. Если 45 минут разделить на 5 шагов поровну, сколько минут на каждый шаг?',
      ruPrimaryText: 'Реши 4 задачи про план на 3 дня.',
      enHelpText: 'Solve 4 three-day plan problems.',
      answer: '1) 3 × 15 = 45 минут. 2) Да, хватит. 3) 45 − 40 = 5 минут. 4) 45 ÷ 5 = 9 минут.' },
    { id: 'draft-check-table', type: 'text', title: 'Задание 6 — Таблица проверки / Draft Check Table',
      prompt: 'Прочитай таблицу черновиков:\n\nКарточка помощи — 4 пункта есть, 1 пункт не хватает.\nПостер пароля — 3 пункта есть, 2 пункта не хватает.\nСхема измерения — 5 пунктов есть, 0 пунктов не хватает.\nМини-гайд карты — 2 пункта есть, 3 пункта не хватает.\n\nОтветь:\n1. У какого черновика всё готово?\n2. У какого черновика больше всего не хватает?\n3. Сколько всего пунктов не хватает у всех черновиков?\n4. Сколько всего готовых пунктов у карточки помощи и схемы измерения?',
      ruPrimaryText: 'Прочитай таблицу 4 черновиков и реши 4 задачи.',
      enHelpText: 'Read the table of 4 drafts and answer 4 questions.',
      answer: '1) Схема измерения. 2) Мини-гайд карты. 3) 1 + 2 + 0 + 3 = 6. 4) 4 + 5 = 9.' },
    { id: 'spelling-draft', type: 'text', title: 'Задание 7 — Правописание / Spelling',
      prompt: 'Напиши:\n\nплан\nшаг\nпример\nпоказ\nготово\n\nВставь буквы:\n\nпл_н\nш_г\nприм_р\nпок_з\nгот_во',
      ruPrimaryText: 'Напиши 5 слов и вставь пропущенные буквы.',
      enHelpText: 'Write 5 words and fill in the missing letters.',
      answer: 'план, шаг, пример, показ, готово.' },
    { id: 'creative-storyboard', type: 'text', title: 'Задание 8 — Storyboard из 4 кадров / 4-Frame Storyboard',
      prompt: 'Сделай план полезного контента из 4 кадров.\n\nКадр 1 — Заголовок.\nКадр 2 — Главная идея.\nКадр 3 — Пример.\nКадр 4 — Что сделать дальше.\n\nВыбери тему:\nA. Как попросить помощи.\nB. Как проверить рекламу.\nC. Как измерить предмет.\nD. Как читать карту.',
      ruPrimaryText: 'Сделай Storyboard из 4 кадров: заголовок, идея, пример, действие.',
      enHelpText: 'Make a 4-frame storyboard: title, main idea, example, next action.',
      answer: 'Open-ended. Accept if Anita creates 4 frames with title, idea, example and next action.' },
    { id: 'draft-improve-choice', type: 'text', title: 'Задание 9 — Как улучшить черновик? / Improve the Draft',
      prompt: 'Выбери, что помогает улучшить черновик.\n\n1. Добавить заголовок.\n2. Стереть всё, потому что не идеально.\n3. Добавить пример.\n4. Проверить с родителем.\n5. Написать личный адрес.\n6. Сделать фразу короче и понятнее.\n7. Пообещать невозможное.\n8. Добавить шаг «спроси взрослого».\n\nНапиши номера хороших вариантов.',
      ruPrimaryText: 'Выбери номера хороших способов улучшить черновик.',
      enHelpText: 'Pick the numbers of good ways to improve the draft.',
      answer: 'Good: 1, 3, 4, 6, 8. Do not use: 2, 5, 7.' },
    { id: 'speech-draft-explain', type: 'text', title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt: 'Скажи родителю по-русски:\n\nЧерновик — это первый шаг.\nЯ могу проверить и исправить.\nМой проект станет понятнее.',
      ruPrimaryText: 'Скажи родителю 3 фразы про черновик и исправление.',
      enHelpText: 'Tell parent 3 phrases about draft and fixing.',
      answer: 'Accept if Anita explains draft/check/fix in Russian with support.' }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Прочитай 4 фразы.',
    '3. Поставь 5 шагов автора в порядок.',
    '4. Реши задачи про время проекта.',
    '5. Прочитай таблицу черновиков.',
    '6. Сделай storyboard из 4 кадров.',
    '7. Выбери хорошие способы улучшить черновик.',
    '8. Объясни родителю, почему черновик полезен.'
  ],
  printWorksheet: {
    title: 'План автора: от идеи к черновику / Creator Plan: From Idea to Draft',
    subtitle: 'Неделя 9 · День 3 / Week 9 · Day 3',
    pages: [
      { title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Нимни показывает папку «План автора». Сегодня ты узнаешь, что черновик — это первый шаг, а не ошибка.' },
          { type: 'text', text: 'Задание 1 — Слова плана. Прочитай и обведи:' },
          { type: 'list', items: [
            'план / plan',
            'черновик / draft',
            'проверка / check',
            'исправление / fix',
            'кадр / frame',
            'заголовок / title',
            'пример / example',
            'шаг / step',
            'показ / show',
            'готово / ready'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку:' },
          { type: 'list', items: [
            '□ Черновик — это первый шаг.',
            '□ Проверка помогает сделать лучше.',
            '□ Автор исправляет и пробует снова.',
            '□ Готовая работа должна быть понятной.'
          ] }
        ] },
      { title: 'План и математика / Plan and Math',
        blocks: [
          { type: 'text', text: 'Задание 3 — Порядок шагов.' },
          { type: 'list', items: [
            'A. Показать родителю.',
            'B. Сделать черновик.',
            'C. Придумать идею.',
            'D. Исправить.',
            'E. Проверить.'
          ] },
          { type: 'text', text: 'Правильный порядок: ____ → ____ → ____ → ____ → ____' },
          { type: 'text', text: 'Задание 4 — Время проекта. Считаем время:' },
          { type: 'list', items: [
            '5 шагов × 8 минут = ______ минут',
            '2 шага × 8 минут = ______ минут уже прошло',
            'Осталось шагов: 5 − 2 = ______',
            'Осталось минут: 3 × 8 = ______'
          ] },
          { type: 'text', text: 'Задание 5 — План на 3 дня. День 1 — 15 минут. День 2 — 15 минут. День 3 — 15 минут.' },
          { type: 'list', items: [
            '3 × 15 = ______ минут',
            'Хватит ли для проекта на 40 минут? да / нет',
            '45 − 40 = ______ минут останется',
            '45 ÷ 5 = ______ минут на шаг'
          ] },
          { type: 'text', text: 'Задание 6 — Таблица проверки. Карточка помощи — 4 есть, 1 не хватает. Постер пароля — 3 есть, 2 не хватает. Схема измерения — 5 есть, 0 не хватает. Мини-гайд карты — 2 есть, 3 не хватает.' },
          { type: 'list', items: [
            '1. Всё готово у: ____________________',
            '2. Больше всего не хватает у: ____________________',
            '3. Всего не хватает: 1 + 2 + 0 + 3 = ______',
            '4. Карточка помощи + схема измерения: 4 + 5 = ______'
          ] }
        ] },
      { title: 'Storyboard / План из 4 кадров',
        blocks: [
          { type: 'text', text: 'Задание 7 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: ['план   шаг   пример   показ   готово', 'пл_н   ш_г   прим_р   пок_з   гот_во'] },
          { type: 'text', text: 'Задание 8 — Storyboard из 4 кадров. Выбери тему:' },
          { type: 'list', items: [
            '□ Как попросить помощи.',
            '□ Как проверить рекламу.',
            '□ Как измерить предмет.',
            '□ Как читать карту.'
          ] },
          { type: 'list', items: [
            'Кадр 1 — Заголовок: ____________________________________',
            'Кадр 2 — Главная идея: ____________________________________',
            'Кадр 3 — Пример: ____________________________________',
            'Кадр 4 — Что сделать дальше: ____________________________________'
          ] },
          { type: 'text', text: 'Задание 9 — Как улучшить черновик? Поставь ✓ у хороших вариантов:' },
          { type: 'list', items: [
            '1. Добавить заголовок. ____',
            '2. Стереть всё, потому что не идеально. ____',
            '3. Добавить пример. ____',
            '4. Проверить с родителем. ____',
            '5. Написать личный адрес. ____',
            '6. Сделать фразу короче и понятнее. ____',
            '7. Пообещать невозможное. ____',
            '8. Добавить шаг «спроси взрослого». ____'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Поставь галочку:' },
          { type: 'list', items: [
            '□ Черновик — это первый шаг.',
            '□ Я могу проверить и исправить.',
            '□ Мой проект станет понятнее.'
          ] },
          { type: 'text', text: 'Parent Check: □ Прочитала слова  □ Прочитала 4 фразы  □ Поставила шаги в порядок  □ Решила задачи времени  □ Сделала storyboard  □ Объяснила черновик родителю.' },
          { type: 'text', text: 'Notebook Mode — если нет принтера, сделай эти задания в тетради.' }
        ] }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает, что создание полезного контента — это процесс: идея, черновик, проверка, исправление, показ родителю.',
    mathGoal: 'Умножение 5 × 8, 2 × 8, 3 × 15, деление 45 ÷ 5, таблица проверки, многошаговые задачи.',
    russianGoal: 'Прочитать слова план, черновик, проверка, исправление, кадр, заголовок, пример, шаг, показ, готово.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 5 слов, заполнить storyboard.',
    practicalGoal: 'Создать план из 4 кадров для полезного контента.',
    parentTheoryCard: {
      title: 'Как объяснить черновик и план автора',
      purpose: 'Помочь родителю объяснить, что хороший результат появляется через маленькие шаги, а не сразу идеально.',
      keyMessage: 'Черновик — это не ошибка. Черновик — это начало работы автора.',
      parentScript: [
        '«Идея — что ты хочешь сделать.»',
        '«Черновик — первая версия.»',
        '«Проверка — где можно сделать понятнее.»',
        '«Исправление — сделать лучше.»',
        '«Показ родителю — объяснить результат.»'
      ],
      practiceSteps: [
        'Поставьте 5 шагов в порядок.',
        'Выберите одну тему storyboard.',
        'Сделайте 4 кадра.',
        'Проверьте: есть ли заголовок, идея, пример, действие.',
        'Попросите Аниту объяснить черновик.'
      ],
      doNotSay: [
        'Не говорить: «Сделай сразу идеально.»',
        'Не сравнивать с Максимом.',
        'Не исправлять всё за ребёнка.',
        'Не просить публиковать онлайн.'
      ],
      paperOnlyNotes: [
        'Storyboard делается на бумаге или в тетради.',
        'Показывать только родителю/семье.'
      ]
    },
    whatThisLessonTeaches: 'Анита учится планировать создание полезного контента и не бояться черновика.',
    keyMessage: 'Я могу начать с черновика, проверить, исправить и сделать понятнее.',
    howToExplain: 'Скажите: «Автор редко делает идеально с первого раза. Черновик нужен, чтобы увидеть идею и потом улучшить.»',
    exampleDialogue:
      'Parent: Черновик — это ошибка?\nChild: Нет, это первый шаг.\nParent: Что делает автор после черновика?\nChild: Проверяет и исправляет.\nParent: Зачем?\nChild: Чтобы было понятнее.',
    readingSupport: 'Слова «черновик», «исправление», «заголовок» читать по частям.',
    russianSpeechGoal: 'Анита говорит: «Черновик — это первый шаг. Я могу проверить и исправить.»',
    mathAnswerKey:
      'math-plan-time: 1) 40. 2) 3. 3) 16. 4) 24.\nmath-three-day-plan: 1) 45. 2) Да. 3) 5. 4) 9.\ndraft-check-table: 1) Схема измерения. 2) Мини-гайд карты. 3) 6. 4) 9.',
    spellingAnswerKey: 'план\nшаг\nпример\nпоказ\nготово',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения.' },
      { taskId: 'creator-steps-order', answer: 'C → B → E → D → A.' },
      { taskId: 'math-plan-time', answer: '40; 3; 16; 24.' },
      { taskId: 'math-three-day-plan', answer: '45; да; 5; 9.' },
      { taskId: 'draft-check-table', answer: 'схема измерения; мини-гайд карты; 6; 9.' },
      { taskId: 'spelling-draft', answer: 'план, шаг, пример, показ, готово.' },
      { taskId: 'creative-storyboard', answer: '4 frames: title, main idea, example, next action.' },
      { taskId: 'draft-improve-choice', answer: '1,3,4,6,8 good; 2,5,7 do not use.' },
      { taskId: 'speech-draft-explain', answer: 'explanation attempted.' }
    ],
    check: [
      'Анита прочитала слова и фразы.',
      'Анита поставила шаги в порядок.',
      'Анита решила math tasks.',
      'Анита сделала storyboard.',
      'Анита выбрала хорошие улучшения.',
      'Анита объяснила родителю.'
    ],
    spellingCheck: 'Проверить план, шаг, пример, показ, готово.',
    readingCheck: 'Главное — попытка и понимание.',
    practicalCheck: 'Storyboard должен быть безопасным, бумажным, без личных данных.',
    drawingCriteria: 'Есть 4 кадра: заголовок, идея, пример, действие дальше.',
    commonMistakes: [
      '«Черновик — это плохо.» Правильно: «Черновик — первый шаг.»',
      '«Надо стереть всё, если не идеально.» Правильно: «Лучше проверить и улучшить.»',
      '«Можно добавить личный адрес для примера.» Правильно: «Личные данные не добавляем.»'
    ],
    discussionPrompts: [
      'Что такое черновик?',
      'Как проверить работу?',
      'Какой кадр самый важный?'
    ],
    ifEasy: 'Попросить Аниту сделать второй storyboard для другой темы.',
    ifDifficult: 'Оставить только 3 кадра: заголовок, идея, пример.',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за storyboard или ясное объяснение черновика.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не публиковать storyboard онлайн. Не использовать личные данные.',
    safetyNotes: 'Все creator drafts остаются paper-only and parent-reviewed. Не использовать реальные адреса, фото, аккаунты или личную информацию.'
  },
  reward: { unit: 'nim', unitName: 'ним', base: 3, max: 5, label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'neat-storyboard', label: 'Бонус за аккуратный storyboard', amount: 1 },
      { id: 'clear-draft-explain', label: 'Бонус за ясное объяснение черновика', amount: 1 }
    ] },
  completionRequirements: {
    requiredTaskIds: ['reading-words', 'reading-sentences', 'creator-steps-order', 'math-plan-time', 'math-three-day-plan', 'draft-check-table', 'spelling-draft', 'creative-storyboard', 'draft-improve-choice', 'speech-draft-explain'],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-9-day-4'
};

export default day3;
